import { useCallback, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useAppSelector,
  getWorkspaceId,
  dispatch,
  useLazyGetInvoiceListQuery,
  Invoice,
  updateSelectedItemsMap,
  APP_TYPE,
  getAppState,
  getExtraDetails,
  updateExtraDetails,
  FilterRoute,
  removeSearchKey,
} from '@zonofi/zono-money-store';
import {
  FIELD_OPS_ROUTES,
  InvoiceStatusEnum,
  SELLER_WEBAPP_ROUTES,
  SortDirection,
} from '../../constants';
import {
  useDebounceSearch,
  usePaginatedList,
  useUrlDateRange,
} from '../../hooks';
import moment from 'moment';
import {
  createBlobObj,
  getInvoiceStatus,
  handleShare,
  invoiceStatusList,
} from '../../helper';
import { debounce } from 'lodash';

export const useInvoiceList = ({
  selectInvoice = false,
  isCustomer = false,
}: {
  selectInvoice?: boolean;
  isCustomer: boolean;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { customerId: customerIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');

  const appState = useAppSelector(getAppState);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const customerDetails = useAppSelector(getExtraDetails);

  const [fetchInvoicesApi, invoicesApiResponse] = useLazyGetInvoiceListQuery();

  const { appliedDateRange } = useUrlDateRange({
    route: isCustomer ? FilterRoute.CustomerInvoice : FilterRoute.Invoice,
  });

  const { debouncedSearchValue } = useDebounceSearch();
  const [appliedStatus, setAppliedStatus] = useState<string[]>([]);

  const lastPath = useMemo(() => pathname.split('/').pop(), [pathname]);

  const statusFilters = useMemo(() => {
    const statusList = invoiceStatusList ?? [];
    const filters = statusList.map((status) => ({
      id: status,
      value: getInvoiceStatus(status),
      selected: appliedStatus.includes(status),
    }));

    filters.unshift({
      id: InvoiceStatusEnum.DEFAULT,
      value: 'All',
      selected: appliedStatus.length === 0,
    });

    return filters;
  }, [appliedStatus, lastPath]);

  const onInvoiceItemPress = (invoice: Invoice) => {
    dispatch(updateSelectedItemsMap(invoice));
    dispatch(
      updateExtraDetails({
        customerName: invoice?.customerName,
        customerId: invoice?.inviteId,
      })
    );
    if (!selectInvoice) {
      const baseRouteToNavigate =
        appState === APP_TYPE.FIELDS_OPS
          ? FIELD_OPS_ROUTES
          : SELLER_WEBAPP_ROUTES;
      if (pathname === baseRouteToNavigate.INVOICES_LIST) {
        navigate(`${baseRouteToNavigate.INVOICES_LIST}/${invoice.id}`);
      }
      if (
        pathname ===
        FIELD_OPS_ROUTES.CUSTOMER_PROFILE_INVOICE.replace(
          ':customerId',
          customerIdParam ?? ''
        )
      ) {
        navigate(`${invoice.id}`);
      }
    }
  };

  const handleSelectAll = (invoiceData: Invoice[]) => {
    if (invoiceData && invoiceData?.length > 0)
      dispatch(updateSelectedItemsMap(invoiceData));
  };

  const memoizedInvoiceData = useMemo(() => {
    return invoicesApiResponse?.data?.invoices ?? [];
  }, [invoicesApiResponse?.data?.invoices]);

  const fetchInvoices = useCallback(
    ({ pageNo, pageSize }: { pageNo: number; pageSize: number }) => {
      fetchInvoicesApi({
        workspaceId: sellerWorkspaceId,
        pageNo,
        pageSize,
        startDate: `${moment(appliedDateRange?.startDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )} 00:00:00`,
        endDate: `${moment(appliedDateRange?.endDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )} 23:59:59`,
        sortBy: 'createdDate',
        sortDirection: SortDirection.DESCENDING,
        ...(appliedStatus &&
          appliedStatus?.length > 0 && { invoiceStatus: appliedStatus }),
        dispatchFilter: true,
        ...(debouncedSearchValue && { searchKey: debouncedSearchValue }),
        ...((customerId || customerIdParam) && {
          inviteId: customerId ?? customerIdParam,
        }),
      });
    },
    [appliedDateRange, appliedStatus, debouncedSearchValue, customerId]
  );

  const {
    data: invoiceData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    refresh,
    isEndOfList,
  } = usePaginatedList<Invoice>({
    fetchDataApiCall: fetchInvoices,
    apiResponseData: memoizedInvoiceData,
    isLoading: invoicesApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      statusFilters,
      debouncedSearchValue,
    ],
    isError: invoicesApiResponse.isError,
    paginationMeta: {
      startRecord: invoicesApiResponse?.data?.startRecord || 0,
      endRecord: invoicesApiResponse?.data?.endRecord || 0,
      totalCount: invoicesApiResponse?.data?.totalInvoices || 0,
    },
  });

  const onSearchCancel = () => {
    dispatch(removeSearchKey());
  };

  const handleShareInvoice = async (invoiceItem: Invoice) => {
    console.log('Sharing invoice:', invoiceItem?.id);
    const invoiceDocUrl = invoiceItem?.docs?.[0]?.url;
    let blob: Blob | undefined;
    if (invoiceDocUrl) {
      blob = await createBlobObj(invoiceDocUrl);
    }

    if (blob) {
      handleShare(
        blob,
        `${customerDetails?.customerName ? `${customerDetails.customerName}_invoice` : `invoice_${invoiceItem?.id}_${Date.now().toString()}`}.pdf`
      );
    } else {
      console.error('Failed to generate invoice blob');
    }
  };

  return {
    invoiceData,
    pageNo,
    lastItemRef,
    isPageLoading,
    lastPath,
    onInvoiceItemPress,
    onScroll,
    statusFilters,
    isLoading:
      pageNo === 1 &&
      (invoicesApiResponse?.isUninitialized || invoicesApiResponse?.isFetching),
    setAppliedStatus,
    handleSelectAll,
    onSearchCancel,
    isEndOfList,
    handleShareInvoice,
    appliedDateRange,
    refreshInvoiceList: refresh,
  };
};
